import React, {
  startTransition,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "/api/public";
const DEFAULT_DESCRIPTION =
  "Editorial SEO de Cursos a Distancia con resenas, comparativas y listicles conectados al directorio.";

function buildApiUrl(pathname, params = {}) {
  const url = new URL(`${API_BASE}${pathname}`, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.pathname + url.search;
}

async function fetchJson(pathname, params = {}, signal) {
  const response = await fetch(buildApiUrl(pathname, params), { signal });
  const payload = await response.json();

  if (!response.ok || !payload.success) {
    throw new Error(payload.message || "No se pudo obtener la informacion.");
  }

  return payload;
}

function clampPage(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function useApiData(pathname, params) {
  const [state, setState] = useState({ data: null, meta: null, loading: true, error: "" });
  const requestKey = JSON.stringify(params);
  const stableParams = useMemo(() => JSON.parse(requestKey), [requestKey]);

  useEffect(() => {
    const controller = new AbortController();

    setState((current) => ({ ...current, loading: true, error: "" }));

    fetchJson(pathname, stableParams, controller.signal)
      .then((payload) => {
        setState({
          data: payload.data,
          meta: payload.meta || null,
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        setState({
          data: null,
          meta: null,
          loading: false,
          error: error.message,
        });
      });

    return () => controller.abort();
  }, [pathname, stableParams]);

  return state;
}

function useDocumentMeta(title, description) {
  useLayoutEffect(() => {
    const previousTitle = document.title;
    const descriptionNode = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionNode ? descriptionNode.getAttribute("content") : null;
    const canonicalHref = `${window.location.origin}${window.location.pathname}`;
    let canonicalNode = document.querySelector('link[rel="canonical"]');
    const createdCanonicalNode = !canonicalNode;
    const previousCanonical = canonicalNode ? canonicalNode.getAttribute("href") : null;

    if (!canonicalNode) {
      canonicalNode = document.createElement("link");
      canonicalNode.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalNode);
    }

    document.title = title;
    if (descriptionNode) {
      descriptionNode.setAttribute("content", description);
    }
    if (canonicalNode) {
      canonicalNode.setAttribute("href", canonicalHref);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionNode && previousDescription !== null) {
        descriptionNode.setAttribute("content", previousDescription);
      }
      if (canonicalNode) {
        if (createdCanonicalNode) {
          canonicalNode.remove();
        } else if (previousCanonical !== null) {
          canonicalNode.setAttribute("href", previousCanonical);
        }
      }
    };
  }, [description, title]);
}

function useStructuredData(identifier, payload) {
  const serializedPayload = payload ? JSON.stringify(payload) : "";

  useLayoutEffect(() => {
    const selector = `script[data-structured-data="${identifier}"]`;
    const existingNode = document.querySelector(selector);

    if (!serializedPayload) {
      if (existingNode) existingNode.remove();
      return undefined;
    }

    const scriptNode = existingNode || document.createElement("script");
    scriptNode.setAttribute("type", "application/ld+json");
    scriptNode.setAttribute("data-structured-data", identifier);
    scriptNode.textContent = serializedPayload;

    if (!existingNode) document.head.appendChild(scriptNode);

    return () => {
      const currentNode = document.querySelector(selector);
      if (currentNode) currentNode.remove();
    };
  }, [identifier, serializedPayload]);
}

function buildListicleStructuredData(item) {
  if (!item) return null;

  const origin = window.location.origin;
  const itemListElement = (item.featured_items || [])
    .filter((entry) => entry?.entity?.path && entry?.entity?.title)
    .map((entry, index) => ({
      "@type": "ListItem",
      position: entry.position || index + 1,
      name: entry.entity.title,
      url: `${origin}${entry.entity.path}`,
    }));

  const graph = [
    {
      "@type": "ItemList",
      name: item.title,
      description: item.description || item.intro || "",
      url: `${origin}${item.path || window.location.pathname}`,
      itemListElement,
    },
  ];

  if ((item.faq || []).length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: item.faq.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

function buildComparisonStructuredData(item) {
  if (!item) return null;

  const origin = window.location.origin;
  const graph = [
    {
      "@type": "Article",
      headline: item.title,
      description: item.description || item.decision_summary || "",
      url: `${origin}${item.path || window.location.pathname}`,
      dateModified: item.updated_at || undefined,
      about: (item.entities || []).map((entry) => ({
        "@type": entry.type === "program" ? "Course" : "EducationalOrganization",
        name: entry.title,
        url: entry.path ? `${origin}${entry.path}` : undefined,
      })),
    },
  ];

  if ((item.faq || []).length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: item.faq.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

function updateSearchParams(currentSearchParams, updates = {}, resetPage = true) {
  const nextParams = new URLSearchParams(currentSearchParams);

  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
  });

  if (resetPage) {
    nextParams.set("page", "1");
  }

  return nextParams;
}

function LoadingBlock({ label = "Cargando informacion..." }) {
  return (
    <section className="loading-block">
      <div className="spinner" />
      <p>{label}</p>
    </section>
  );
}

function ErrorBlock({ message }) {
  return (
    <section className="error-block">
      <h2>No pudimos cargar esta seccion</h2>
      <p>{message}</p>
    </section>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <section className="empty-state">
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </section>
  );
}

function Hero({ eyebrow, title, description, aside }) {
  return (
    <section className="hero">
      <div className="hero-copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <aside className="hero-aside">{aside}</aside>
    </section>
  );
}

function StatCard({ label, value }) {
  return (
    <article className="stat-card">
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function Pagination({ meta, onPageChange }) {
  if (!meta || meta.total_pages <= 1) {
    return null;
  }

  const pages = [];
  const start = Math.max(1, meta.page - 2);
  const end = Math.min(meta.total_pages, meta.page + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return (
    <nav className="pagination" aria-label="Paginacion">
      <button type="button" onClick={() => onPageChange(meta.page - 1)} disabled={meta.page <= 1}>
        Anterior
      </button>
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          className={page === meta.page ? "is-active" : ""}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onPageChange(meta.page + 1)}
        disabled={meta.page >= meta.total_pages}
      >
        Siguiente
      </button>
    </nav>
  );
}

function EditorialCard({ item, eyebrow, extra }) {
  return (
    <article className="card editorial-card">
      <div className="card-body">
        <div className="card-tags">
          <span>{eyebrow}</span>
          {item.country ? <span>{item.country}</span> : null}
        </div>
        <h3>
          <Link to={item.path}>{item.title}</Link>
        </h3>
        <p>{item.description || item.decision_summary || item.summary || ""}</p>
        {item.entity ? (
          <p className="editorial-meta">
            {item.entity.title} {item.rating_overall ? `| Rating editorial ${item.rating_overall}` : ""}
          </p>
        ) : null}
        {extra}
      </div>
      <div className="card-footer">
        <Link className="button button-primary" to={item.path}>
          Ver detalle
        </Link>
      </div>
    </article>
  );
}

function SectionList({ title, items, eyebrow }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
      </div>
      <section className="grid-layout">
        {items.map((item) => (
          <EditorialCard key={item.slug} item={item} eyebrow={eyebrow} />
        ))}
      </section>
    </section>
  );
}

function ProseSections({ sections }) {
  return (
    <section className="article-layout">
      {(sections || []).map((section) => (
        <article className="panel" key={section.heading}>
          <h2>{section.heading}</h2>
          <div className="prose-copy">
            {(section.body || []).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
}

function FAQBlock({ items }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <article className="panel">
      <h2>Preguntas frecuentes</h2>
      <div className="faq-list">
        {items.map((item) => (
          <div key={item.question}>
            <strong>{item.question}</strong>
            <p>{item.answer}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function ComparisonTable({ item }) {
  if (!item?.table_rows?.length || !item?.entities?.length) {
    return null;
  }

  return (
    <article className="panel comparison-matrix">
      <h2>Comparación por criterio</h2>
      <div className="table-scroll">
        <table>
          <caption>Resumen de diferencias entre {item.entities.map((entry) => entry.title).join(" y ")}</caption>
          <thead>
            <tr>
              <th scope="col">Criterio</th>
              {item.entities.map((entry) => (
                <th scope="col" key={entry.id}>{entry.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.table_rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                {item.entities.map((entry) => (
                  <td key={`${row.label}-${entry.id}`}>{row.values?.[String(entry.id)] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function CommercialPathPanel({ items }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <article className="panel commercial-path-panel">
      <span className="eyebrow">Siguiente paso</span>
      <h2>Explora la oferta y elige dónde estudiar</h2>
      <p>Después de leer la selección, continúa hacia el hub, la institución o la comparativa que corresponde a tu decisión.</p>
      <div className="link-stack">
        {items.map((item) => (
          <Link key={`${item.href}-${item.label}`} to={item.href}>{item.label}</Link>
        ))}
      </div>
    </article>
  );
}

export function EntityEditorialSection({ profile, relatedContent }) {
  if (!profile && !relatedContent) {
    return null;
  }

  const hasProfile = profile && (profile.editorial_summary || profile.editorial_rating);

  return (
    <section className="page">
      {hasProfile ? (
        <section className="detail-grid editorial-entity-grid">
          <article className="panel">
            <span className="eyebrow">Editorial</span>
            <h2>Lectura editorial</h2>
            <p>{profile.editorial_summary || profile.positioning}</p>
            <div className="chip-row">
              {profile.editorial_rating ? <span>Rating {profile.editorial_rating}</span> : null}
              {profile.verification?.status ? <span>{profile.verification.status}</span> : null}
            </div>
          </article>
          <article className="panel">
            <h2>Fortalezas y alertas</h2>
            <div className="detail-grid editorial-mini-grid">
              <div>
                <strong>Fortalezas</strong>
                <ul className="rich-list">
                  {(profile.strengths || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Alertas</strong>
                <ul className="rich-list">
                  {(profile.cautions || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        </section>
      ) : null}

      <div className="detail-grid editorial-entity-grid">
        <RelatedContentPanel title="Resenas relacionadas" items={relatedContent?.reviews} />
        <RelatedContentPanel title="Comparativas relacionadas" items={relatedContent?.comparisons} />
        <RelatedContentPanel title="Listicles relacionados" items={relatedContent?.listicles} />
      </div>
    </section>
  );
}

function RelatedContentPanel({ title, items }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <article className="panel">
      <h2>{title}</h2>
      <div className="link-stack">
        {items.map((item) => (
          <Link key={item.path} to={item.path}>
            {item.title}
          </Link>
        ))}
      </div>
    </article>
  );
}

export function EditorialHubPage() {
  const hubState = useApiData("/editorial", {});
  const hub = hubState.data;

  useDocumentMeta("Editorial SEO | Cursos a Distancia", DEFAULT_DESCRIPTION);

  if (hubState.loading && !hub) {
    return <LoadingBlock label="Cargando editorial..." />;
  }

  if (hubState.error) {
    return <ErrorBlock message={hubState.error} />;
  }

  return (
    <div className="page">
      <Hero
        eyebrow="Editorial"
        title="Resenas, comparativas y listicles conectados al directorio"
        description="La capa editorial queda versionada en archivos y se publica con la misma pipeline del catalogo. Cada pieza enlaza a entidades reales y a clusters SEO explicitos."
        aside={
          <div className="stats-grid">
            <StatCard label="Resenas" value={hub?.overview?.reviews || "-"} />
            <StatCard label="Comparativas" value={hub?.overview?.comparisons || "-"} />
            <StatCard label="Listicles" value={hub?.overview?.listicles || "-"} />
          </div>
        }
      />

      <section className="detail-grid editorial-entity-grid">
        <article className="panel">
          <h2>Metodologia</h2>
          <p>{hub?.methodology?.description}</p>
          <Link className="button button-primary" to="/resenas">
            Ver resenas
          </Link>
        </article>
        <article className="panel">
          <h2>Ownership de queries</h2>
          <div className="link-stack">
            {(hub?.query_ownership || []).slice(0, 5).map((item) => (
              <Link key={item.path} to={item.path}>
                {item.query}
              </Link>
            ))}
          </div>
        </article>
      </section>

      <SectionList title="Resenas destacadas" eyebrow="Resenas" items={hub?.featured_reviews || []} />
      <SectionList
        title="Comparativas destacadas"
        eyebrow="Comparativas"
        items={hub?.featured_comparisons || []}
      />
      <SectionList title="Listicles destacados" eyebrow="Mejores" items={hub?.featured_listicles || []} />
    </div>
  );
}

function EditorialIndexPage({ endpoint, eyebrow, title, description, emptyTitle, routeBase }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = clampPage(searchParams.get("page"), 1);
  const entityType = searchParams.get("entity_type") || "";
  const qParam = searchParams.get("q") || "";
  const [searchDraft, setSearchDraft] = useState(qParam);
  const deferredSearch = useDeferredValue(searchDraft);

  useEffect(() => {
    setSearchDraft(qParam);
  }, [qParam]);

  useEffect(() => {
    if (deferredSearch === qParam) {
      return;
    }

    startTransition(() => {
      setSearchParams(updateSearchParams(searchParams, { q: deferredSearch }));
    });
  }, [deferredSearch, qParam, searchParams, setSearchParams]);

  const state = useApiData(endpoint, {
    page,
    page_size: 12,
    entity_type: entityType,
    q: qParam,
  });

  return (
    <div className="page">
      <Hero eyebrow={eyebrow} title={title} description={description} aside={null} />

      <section className="filters-bar editorial-filters">
        <label className="field">
          <span>Buscar</span>
          <input
            type="search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Titulo, entidad o query"
          />
        </label>
        <label className="field">
          <span>Tipo de entidad</span>
          <select
            value={entityType}
            onChange={(event) =>
              setSearchParams(updateSearchParams(searchParams, { entity_type: event.target.value }))
            }
          >
            <option value="">Todas</option>
            <option value="institution">Instituciones</option>
            <option value="program">Programas</option>
          </select>
        </label>
      </section>

      {state.loading && !state.data ? <LoadingBlock /> : null}
      {state.error ? <ErrorBlock message={state.error} /> : null}
      {state.data && state.data.length === 0 ? (
        <EmptyState title={emptyTitle} description="No hay piezas para ese filtro por ahora." />
      ) : null}
      {state.data && state.data.length > 0 ? (
        <>
          <section className="grid-layout">
            {state.data.map((item) => (
              <EditorialCard key={item.slug} item={{ ...item, path: item.path || `${routeBase}/${item.slug}` }} eyebrow={eyebrow} />
            ))}
          </section>
          <Pagination
            meta={state.meta}
            onPageChange={(nextPage) =>
              setSearchParams(updateSearchParams(searchParams, { page: String(nextPage) }, false))
            }
          />
        </>
      ) : null}
    </div>
  );
}

export function ReviewsPage() {
  useDocumentMeta("Resenas educativas | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/reviews"
      eyebrow="Resenas"
      title="Resenas editoriales"
      description="Piezas objetivas para atacar intencion de opiniones, review y vale la pena."
      emptyTitle="No hay resenas para este filtro"
      routeBase="/resenas"
    />
  );
}

export function ComparisonsPage() {
  useDocumentMeta("Comparativas educativas | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/comparisons"
      eyebrow="Comparativas"
      title="Comparativas SEO"
      description="Paginas para queries tipo vs, con escenarios y diferencias reales entre entidades."
      emptyTitle="No hay comparativas para este filtro"
      routeBase="/comparativas"
    />
  );
}

export function ListiclesPage() {
  useDocumentMeta("Mejores cursos e instituciones | Cursos a Distancia", DEFAULT_DESCRIPTION);
  return (
    <EditorialIndexPage
      endpoint="/listicles"
      eyebrow="Mejores"
      title="Listicles y selecciones editoriales"
      description="Paginas curatoriales para capturar intencion tipo mejores, top y seleccion."
      emptyTitle="No hay listicles para este filtro"
      routeBase="/mejores"
    />
  );
}

function EditorialDetailPage({ endpoint, slug, titleBuilder, descriptionBuilder, schemaBuilder, renderBody }) {
  const state = useApiData(endpoint, { slug });
  const item = state.data;

  useDocumentMeta(titleBuilder(item), descriptionBuilder(item));
  useStructuredData(`${endpoint.replace("/", "")}-detail`, item && schemaBuilder ? schemaBuilder(item) : null);

  if (state.loading && !item) {
    return <LoadingBlock />;
  }

  if (state.error) {
    return <ErrorBlock message={state.error} />;
  }

  if (!item) {
    return <EmptyState title="Contenido no encontrado" description="La pieza solicitada no existe." />;
  }

  return renderBody(item);
}

export function ReviewDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/reviews"
      slug={slug}
      titleBuilder={(item) => (item ? `${item.title} | Cursos a Distancia` : "Resena | Cursos a Distancia")}
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => (
        <div className="page">
          <Hero
            eyebrow="Resena"
            title={item.title}
            description={item.summary || item.description}
            aside={
              <div className="stats-grid">
                <StatCard label="Rating" value={item.rating_overall || "-"} />
                <StatCard label="Entidad" value={item.entity?.title || "-"} />
              </div>
            }
          />
          <section className="detail-grid editorial-entity-grid">
            <article className="panel">
              <h2>Veredicto editorial</h2>
              <p>{item.verdict}</p>
              <div className="chip-row">
                {(item.recommended_for || []).map((value) => (
                  <span key={value}>{value}</span>
                ))}
              </div>
            </article>
            <article className="panel">
              <h2>Senales de confianza</h2>
              <ul className="rich-list">
                {(item.trust_signals || []).map((value) => (
                  <li key={value}>{value}</li>
                ))}
              </ul>
            </article>
          </section>
          <ProseSections sections={item.sections} />
          <FAQBlock items={item.faq} />
          <div className="detail-grid editorial-entity-grid">
            <RelatedContentPanel title="Comparativas relacionadas" items={item.related_content?.comparisons} />
            <RelatedContentPanel title="Listicles relacionados" items={item.related_content?.listicles} />
            <RelatedContentPanel title="Programas relacionados" items={item.related_content?.programs} />
          </div>
        </div>
      )}
    />
  );
}

export function ComparisonDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/comparisons"
      slug={slug}
      schemaBuilder={buildComparisonStructuredData}
      titleBuilder={(item) =>
        item ? `${item.title} | Cursos a Distancia` : "Comparativa | Cursos a Distancia"
      }
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => (
        <div className="page">
          <Hero
            eyebrow="Comparativa"
            title={item.title}
            description={item.decision_summary || item.description}
            aside={<div className="stats-grid">{(item.entities || []).map((entry) => <StatCard key={entry.id} label={entry.type} value={entry.title} />)}</div>}
          />
          <article className="panel">
            <h2>Escenarios ganadores</h2>
            <div className="faq-list">
              {(item.winner_by_scenario || []).map((entry) => (
                <div key={entry.scenario}>
                  <strong>{entry.scenario}</strong>
                  <p>
                    {entry.winner?.title}: {entry.reason}
                  </p>
                </div>
              ))}
            </div>
          </article>
          <ComparisonTable item={item} />
          <ProseSections sections={item.sections} />
          <FAQBlock items={item.faq} />
          <CommercialPathPanel items={item.commercial_path} />
          <div className="detail-grid editorial-entity-grid">
            <RelatedContentPanel title="Resenas relacionadas" items={item.related_content?.reviews} />
            <RelatedContentPanel title="Instituciones comparadas" items={item.entities} />
            <RelatedContentPanel title="Programas relacionados" items={item.related_content?.programs} />
          </div>
        </div>
      )}
    />
  );
}

export function ListicleDetailPage() {
  const { slug } = useParams();

  return (
    <EditorialDetailPage
      endpoint="/listicles"
      slug={slug}
      schemaBuilder={buildListicleStructuredData}
      titleBuilder={(item) => (item ? `${item.title} | Cursos a Distancia` : "Mejores | Cursos a Distancia")}
      descriptionBuilder={(item) => item?.description || DEFAULT_DESCRIPTION}
      renderBody={(item) => (
        <div className="page">
          <Hero
            eyebrow="Mejores"
            title={item.title}
            description={item.intro || item.description}
            aside={<div className="stats-grid"><StatCard label="Items" value={item.featured_items?.length || 0} /></div>}
          />
          <section className="grid-layout">
            {(item.featured_items || []).map((entry) => (
              <article className="card editorial-card" key={`${entry.position}-${entry.entity.id}`}>
                <div className="card-body">
                  <div className="card-tags">
                    <span>Puesto {entry.position}</span>
                    <span>{entry.entity.type === "institution" ? "Institucion" : "Programa"}</span>
                  </div>
                  <h3>
                    <Link to={entry.entity.path}>{entry.entity.title}</Link>
                  </h3>
                  <p>{entry.why_it_made_the_list}</p>
                  <p className="editorial-meta">{entry.best_for}</p>
                </div>
              </article>
            ))}
          </section>
          <ProseSections sections={item.sections} />
          <FAQBlock items={item.faq} />
          <CommercialPathPanel items={item.commercial_path} />
          <div className="detail-grid editorial-entity-grid">
            <RelatedContentPanel title="Resenas relacionadas" items={item.related_content?.reviews} />
            <RelatedContentPanel title="Comparativas relacionadas" items={item.related_content?.comparisons} />
          </div>
        </div>
      )}
    />
  );
}
